// Shared derivation for the per-row status of a bulk landlord-research run, used by
// both /leads/search (LeadSearchPage) and /leads (LeadsPage) so the two surfaces agree.
//
// The bulk worker researches the launch-ordered ref list ONE row at a time, bumping the
// operation's progress.current as each row finishes. So a row's index against `current`
// tells you where it stands: rows before it are done, the row AT it is being researched
// now, rows after it are queued. progress.processed carries the ACTIVE row's live search
// count. This is driven by the single bulk-op poll every surface already runs - there is
// no per-row polling (see building-manager-api operation_landlord_research_bulk.py).

export type BulkResearchRowState =
  | "done"
  | "researching"
  | "queued"
  | "cancelled"
  | null;

// `null` = this ref is not part of the in-flight bulk batch (or there is none).
// `cancelledRefs` is the operator's optimistic set of rows pulled from the queue via
// the per-row Cancel: those override the index math so a cancelled row stops showing
// queued/researching (and doesn't flip to "done" once the worker skips past it).
export function bulkResearchRowStateForRef(
  ref: string,
  refs: string[] | null | undefined,
  current: number,
  cancelledRefs?: ReadonlySet<string> | null
): BulkResearchRowState {
  if (!refs) return null;
  const idx = refs.indexOf(ref);
  if (idx < 0) return null;
  if (cancelledRefs?.has(ref)) return "cancelled";
  if (idx < current) return "done";
  if (idx === current) return "researching";
  return "queued";
}

// The single chip a left-list row shows for its research state. Only these four
// (plus null) render; the bulk run's "done"/"cancelled" carry no visible chip.
export type ResearchRowChip = "researching" | "queued" | "not-run" | null;

// Combine every research signal a row can carry into ONE chip state, so
// /leads/search and /leads render the research chip identically (the lead
// pipeline's "surfaces that must agree" rule). It layers on top of
// bulkResearchRowStateForRef:
//   - a single right-panel "Research landlord" in flight for this row,
//   - the in-flight bulk sub-batch (done/researching/queued),
//   - rows selected into the auto-continue chain that are waiting for a LATER
//     batch (the current sub-batch's overflow) -> still "queued",
//   - rows a finished chain never reached (auto-continue's no-progress guard
//     tripped, or a hard stop) -> a persistent "not-run" chip.
export function researchRowChipState(params: {
  ref: string;
  // A single right-panel research run currently in flight for this row.
  singleResearchingRef?: string | null;
  // The in-flight bulk sub-batch: the queued refs, the worker's cursor, and the
  // rows the operator pulled from the queue.
  bulkRefs?: string[] | null;
  bulkCurrent?: number;
  bulkCancelledRefs?: ReadonlySet<string> | null;
  // Rows in the active chain that are waiting for a later auto-continue batch.
  chainPendingRefs?: ReadonlySet<string> | null;
  // Rows a finished chain never reached - the "not run - run again" markers.
  notRunRefs?: ReadonlySet<string> | null;
}): ResearchRowChip {
  const {
    ref,
    singleResearchingRef,
    bulkRefs,
    bulkCurrent = 0,
    bulkCancelledRefs,
    chainPendingRefs,
    notRunRefs,
  } = params;
  // Active single-row work always wins over a persisted "not run" marker.
  if (singleResearchingRef && ref === singleResearchingRef)
    return "researching";
  const bulk = bulkResearchRowStateForRef(
    ref,
    bulkRefs,
    bulkCurrent,
    bulkCancelledRefs
  );
  if (bulk === "researching") return "researching";
  if (bulk === "queued") return "queued";
  // "done"/"cancelled" render nothing; only a ref that isn't in the current
  // sub-batch at all (bulk === null) can be a future-chain or unreached row.
  if (bulk === null) {
    if (chainPendingRefs?.has(ref)) return "queued";
    if (notRunRefs?.has(ref)) return "not-run";
  }
  return null;
}

// The shape of a completed bulk-research operation's `result` payload (from the
// BE worker in operation_landlord_research_bulk.py). Only the fields the FE
// continuation logic reads are typed.
export interface BulkResearchResultShape {
  status?: string;
  researched?: string[];
  researchedCount?: number;
  failed?: { sourceRef?: string }[];
  notProcessed?: string[];
}

export interface BulkContinuationPlan {
  // "continue" -> re-launch research for `remaining`; "stop" -> flag `remaining`
  // as "not run" and surface the tail; "none" -> clean finish, nothing to do.
  action: "continue" | "stop" | "none";
  // Rows still needing research (to re-launch on "continue", or flag on "stop").
  remaining: string[];
  // Hard failures in THIS batch (the caller accumulates across the chain).
  batchFailedCount: number;
}

// Decide what a completed bulk-research sub-batch should do next. Shared by
// /leads/search and /leads so the two surfaces can never drift (the completion
// logic used to be copy-pasted into both).
//
// `inputRefs` is what THIS batch was asked to research. The worker caps its own
// input to BULK_MAX_ROWS and only reports the wall-clock tail of the capped batch
// in `notProcessed` - it never reports the rows selected past the cap. So "what's
// left" is derived from what actually got DONE (researched + hard-failed), never
// from `notProcessed`; deriving it from notProcessed would silently drop every
// selected row past the cap. The strict-shrink guard (remaining < inputRefs) is
// the no-progress stop: a batch that finished nothing never re-launches, so the
// chain can't loop forever.
export function planBulkResearchContinuation(
  result: BulkResearchResultShape,
  inputRefs: string[],
  cancelledRefs: ReadonlySet<string>
): BulkContinuationPlan {
  const batchFailedCount = Array.isArray(result.failed)
    ? result.failed.length
    : 0;
  // `researched` is the authoritative "these finished" list. If it is somehow
  // absent (a BE too old to return it - not possible with the paired backend,
  // but guard anyway), we can't tell which rows finished, so fall back to the
  // conservative `notProcessed` set rather than mis-flagging every researched
  // row as "not run".
  const researchedList = Array.isArray(result.researched)
    ? result.researched
    : null;
  let remaining: string[];
  if (researchedList) {
    const done = new Set<string>(researchedList.filter(Boolean));
    (result.failed ?? []).forEach((f) => {
      if (f?.sourceRef) done.add(f.sourceRef);
    });
    remaining = inputRefs.filter((r) => !done.has(r) && !cancelledRefs.has(r));
  } else {
    remaining = (
      Array.isArray(result.notProcessed) ? result.notProcessed : []
    ).filter((r) => !cancelledRefs.has(r));
  }
  if (remaining.length > 0 && remaining.length < inputRefs.length) {
    return { action: "continue", remaining, batchFailedCount };
  }
  if (remaining.length > 0 || batchFailedCount > 0) {
    return { action: "stop", remaining, batchFailedCount };
  }
  return { action: "none", remaining: [], batchFailedCount };
}
